import React, { useContext, useEffect, useRef, useState } from "react";
import { StandardCase } from "@src/lms/jelly/extensions/layouts/standard-case/StandardCase";

import { Accordion, AccordionItem } from "@src/lms/jelly/extensions/ui";
import { useIntl } from "react-intl";

import { messages } from "./TechnicalProcess.messages";
import styles from "./TechnicalProcess.module.scss";

import * as appConfig from "appConfig";
import { PhotoView } from "../../../photo-view/PhotoView";
import { AccordionContext } from "@src/lms/jelly/extensions/ui/accordion/AccordionContext";

interface TechnicalProcess {
  title: string;
}

type AccordionItem = "default" | "layout" | "signal-flow";

interface CreativeProcessContextData {
  setActiveSlide: (slide: AccordionItem) => void;
  activeSlide: AccordionItem;
}

const CreativeProcessContext = React.createContext<CreativeProcessContextData>({
  activeSlide: "default",
  setActiveSlide: () => {
    throw new Error("Not implemented!");
  },
});

export const TechnicalProcess: React.FC<TechnicalProcess> = ({ title }) => {
  const [activeSlide, setActiveSlide] = useState<AccordionItem>("default");

  return (
    <CreativeProcessContext.Provider
      value={{
        setActiveSlide,
        activeSlide,
      }}
    >
      <TechnicalProcessContent title={title} />
    </CreativeProcessContext.Provider>
  );
};

const TechnicalProcessContent: React.FC<TechnicalProcess> = ({ title }) => {
  const { activeSlide } = useContext(CreativeProcessContext);
  const { formatMessage } = useIntl();

  let content: React.ReactNode = activeSlide;

  switch (activeSlide) {
    case "default":
      content = (
        <img
          style={{ width: "95%" }}
          src={`${appConfig.STATIC_ASSETS_URL}/lms/case/case02/technical-process/speakers-layout.svg`}
          alt={"room"}
        />
      );
      break;
    case "layout":
      content = (
        <img
          style={{ width: "95%" }}
          src={`${appConfig.STATIC_ASSETS_URL}/lms/case/case02/technical-process/speakers-layout.svg`}
          alt={"room"}
        />
      );
      break;
    case "signal-flow":
      content = (
        <PhotoView
          key={"logical"}
          imgSrc={`${appConfig.STATIC_ASSETS_URL}/lms/case/case02/technical-process/signal-flow.svg`}
          imgMode={"contain"}
        />
      );
      break;
  }

  const accordion = (
    <Accordion allowMultiOpen={false} defaultOpen={[]}>
      <MainAccordionObserver />
      <AccordionItem id="layout" title={formatMessage(messages.accordionItem1Title)} unmountChildrenWhenHidden>
        <p>{formatMessage(messages.accordionItem1Description)}</p>
      </AccordionItem>
      <AccordionItem id="signal-flow" title={formatMessage(messages.accordionItem2Title)} unmountChildrenWhenHidden>
        <p>{formatMessage(messages.accordionItem2Description)}</p>
      </AccordionItem>
    </Accordion>
  );

  return (
    <StandardCase title={title} leftComponent={accordion}>
      <div className={styles.content}>{content}</div>
    </StandardCase>
  );
};

const MainAccordionObserver: React.FC = () => {
  const { setActiveSlide } = useContext(CreativeProcessContext);
  const accordionContext = useContext(AccordionContext);
  const previouslyOpen = useRef<string | null>(null);

  useEffect(() => {
    const openItem = accordionContext.openIds[0] ?? null;
    const userOpenedAnotherItem = previouslyOpen.current != openItem;
    if (userOpenedAnotherItem) {
      previouslyOpen.current = openItem;
      let slide: AccordionItem = "default";
      switch (openItem) {
        case "layout":
          slide = "layout";
          break;
        case "signal-flow":
          slide = "signal-flow";
          break;
      }

      setActiveSlide(slide);
    }
  }, [accordionContext.openIds, setActiveSlide]);

  return null;
};
